(ns rothko.editor.space.transform
  (:require [com.rpl.specter :as sp]
            [rothko.editor.document.navigator :refer [selectionnav]]
            [rothko.editor.document.navigators :as n]
            [rothko.editor.document.paths :as document-paths]
            [rothko.editor.edit.paths :as edit-paths]
            [rothko.editor.edit.transform :as edit-transform]
            [rothko.editor.space.paths :as paths]
            [rothko.editor.matcher :as m]
            [rothko.editor.word.paths :as word-paths]
            [rothko.editor.word.transform :as word-transform]))

(def space
  {:word " "
   :type :space})

(def space-edit
  {:word " "
   :type :space-edit})

(defn new-token
  [space-edits?]
  (if space-edits? space-edit space))

(defn removable-space
  [space-edits?]
  (if space-edits? :space-edit :space))

(defn create*
  [index token]
  [:script
   (sp/before-index index)
   (sp/terminal-val token)])

(defn token-range->selected-word
  [token-range start-offset end-offset]
  (->> token-range
       (mapv :word)
       (apply str)
       (take end-offset)
       (drop start-offset)
       (apply str)))

(defn space-split
  []
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    (selectionnav
     []
     (transform*
      [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
      (sp/transform
       (sp/multi-path
        [(document-paths/collect-surrounds selection)
         :script
         (sp/srange start-index (inc end-index))
         (sp/terminal
          (fn [[first-to-offset offset-to-last] token-range]
            (let [token (n/merge-tokens token-range)
                  selected-word (token-range->selected-word token-range start-offset end-offset)]
              (cond-> []
                      (not-empty first-to-offset) (conj (assoc token :word first-to-offset))
                      :always (conj (assoc token :word selected-word :edited? true))
                      (not-empty offset-to-last) (conj (assoc token :word offset-to-last))))))]
        [:selection
         (sp/terminal-val
          (assoc selection :end-offset start-offset))])
       next-fn
       structure)))]))

(defn edit-split
  [separating-token keep-system-flag?]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (sp/transform
        [(document-paths/collect-surrounds selection)
         (sp/multi-path
          [:script
           (sp/srange start-index (inc end-index))
           (sp/terminal
            (fn [[first-to-offset offset-to-last] [first-token :as token-range]]
              (let [token (n/merge-tokens token-range keep-system-flag?)]
                (->> [(assoc token :word first-to-offset
                                   :type (:type first-token))
                      separating-token
                      (assoc token :word offset-to-last)]
                     (filter #(-> % :word seq))))))]
          [:selection
           (sp/terminal
            (fn [[first-to-offset] _]
              (let [index (cond-> start-index (seq first-to-offset) inc)]
                {:start-index index
                 :start-offset 1
                 :end-index index
                 :end-offset 1})))])]
        next-fn
        structure)))]))

(def split-space-and-insert-edit*
  (selectionnav
   [chars]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (sp/transform
     [(sp/collect-one n/surrounds)
      (sp/multi-path
       [:script
        (sp/srange start-index (inc end-index))
        (sp/terminal
         (fn [[first-to-offset offset-to-last] _]
           [{:word first-to-offset
             :type :space}
            {:word chars
             :type :edit}
            {:word offset-to-last
             :type :space}]))]
       [:selection
        (sp/terminal-val
         {:start-index (inc start-index)
          :start-offset (count chars)
          :end-index (inc start-index)
          :end-offset (count chars)})])]
     next-fn
     structure))))

(defn split-space-and-insert-edit
  [chars]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (split-space-and-insert-edit* chars)]))

(defn tokens->edited-tokens
  [tokens]
  (mapv #(assoc % :edited? true) tokens))

(def edit-selection-and-add-new-chars*
  (selectionnav
    [chars]
    (transform*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (sp/transform
        [(sp/multi-path
           [:script
              (sp/srange start-index (inc end-index))
              (sp/terminal (fn [tokens] (-> tokens
                                            tokens->edited-tokens
                                            (conj {:word chars
                                                   :type :edit}))))]
           [:selection
            (sp/terminal-val
              {:start-index  (inc start-index)
               :start-offset (count chars)
               :end-index    (inc start-index)
               :end-offset   (count chars)})])]
        next-fn
        structure))))

(defn edit-selection-and-add-new-chars
  "When the replacement target is an in-between space
   mark the space as edited? and append a new token.
   Very similar to 'split-space-and-insert-edit'."
  [chars]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (edit-selection-and-add-new-chars* chars)]))

(defn contiguous-spaces
  [{:keys [start-index start-offset end-index end-offset] :as selection}]
  [:script
   (sp/srange-dynamic
    (constantly start-index)
    (fn [script]
      (min (count script)
           (inc end-index))))
   (sp/continuous-subseqs
    (fn [{:keys [type]}]
      (= :space type)))])

(defn collapse-all
  [{:keys [start-index start-offset end-index end-offset] :as selection}]
  [(sp/collect
    (contiguous-spaces selection))
   (sp/multi-path
    [(contiguous-spaces selection)
     (sp/terminal
      (fn [_ _]
        [{:word " "
          :type :space}]))]
    [:selection
     :end-index
     (sp/terminal
      (fn [continuous-spaces end-index]
        (- end-index
           (transduce
            (comp (map count)
                  (map dec))
            +
            continuous-spaces))))])])

(defn remove-trailing-space-from-selection
  [space-type? recursively?]
  (sp/transformed
   [(sp/collect-one
     n/selected)
    :selection]
   (fn [token-range {:keys [end-index] :as selection}]
     (let [number (count token-range)]
       (if (and (< 1 number)
                (space-type? (:type (nth token-range (dec number)))))
         (let [new-selection (assoc selection
                               :end-index (dec end-index)
                               :end-offset (-> token-range
                                               (nth (- number 2))
                                               (:word)
                                               (count)))]
           (if recursively?
             (recur (pop token-range) new-selection)
             new-selection))
         selection)))))

(def remove-leading-space-from-selection
  (sp/transformed
   [(sp/collect-one
     n/selected)
    :selection]
   (fn [token-range {:keys [start-index] :as selection}]
     (if (and (< 1 (count token-range))
              (= :space (:type (nth token-range 0))))
       (assoc selection
              :start-index (inc start-index)
              :start-offset 0)
       selection))))

(def remove-dangling-spaces
  (n/if-caret?
   sp/STAY
   [remove-leading-space-from-selection
    (remove-trailing-space-from-selection #{:space} false)]))

(defn create
  [token]
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    (selectionnav
      []
      (transform*
       [_ {:keys [end-index] :as selection} structure next-fn]
       (sp/transform
        (sp/multi-path
         (create* end-index token)
         [:selection
          (sp/multi-path
           [:start-offset
            (sp/terminal-val
             1)]
           [:end-offset
            (sp/terminal-val
             1)])])
        next-fn
        structure)))]))

(def insert*
  (selectionnav
   [shift token]
   (transform*
    [_ {:keys [end-index]} structure next-fn]
    (sp/transform
     (sp/multi-path
      (create* (+ end-index shift) token)
      [:selection
       (sp/terminal
        (fn [& _]
          {:start-index (inc end-index)
           :start-offset 1
           :end-index (inc end-index)
           :end-offset 1}))])
     next-fn
     structure))))

(defn insert
  [shift token]
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    (insert* shift token)]))
